import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Sparkles, Send, FileText, AlertCircle, Check, X, ClipboardList, Mic, Square, Camera,
  ChevronRight,
} from 'lucide-react'
import clsx from 'clsx'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import PageHeader from '../../components/PageHeader'
import KraalFeedCard from '../../components/dashboard/KraalFeedCard'
import { useAuth } from '../../contexts/AuthContext'
import {
  askAssistant, askAssistantStream, askAssistantVoice,
  recordAssistant, confirmAssistant, recordVoice, recordPhoto,
  getWeeklyReports, getWeeklyReport, updateUser,
} from '../../services/api'

// Assistant languages (2026-07-04): answers follow the selected language;
// voice questions are transcribed with the matching hint. Runyankole speech
// recognition is approximate (no dedicated model exists — Kinyarwanda proxies
// it), so the assistant interprets intent and asks when unsure.
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'lg', label: 'Luganda' },
  { code: 'nyn', label: 'Runyankole' },
]

// Best available capture format: Opus (webm/ogg) is what the transcriber
// supports. Safari records AAC-in-MP4, which it can't decode — surface
// "unsupported" honestly rather than uploading garbage.
function pickAudioMime() {
  if (typeof MediaRecorder === 'undefined') return null
  for (const m of ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']) {
    if (MediaRecorder.isTypeSupported?.(m)) return m
  }
  return null
}

// Starter prompts for Ask mode — each sends on click.
const STARTERS = [
  'How many active animals do I have?',
  'Any deaths or losses this week?',
  'Which animals are overdue for vaccination?',
  'How is net profit trending this month?',
]

// Examples for Record mode — clicking fills the box (recording is consequential,
// so we don't auto-send).
const RECORD_HINTS = [
  'weight Bessie 350',
  'the brown cow looks sick',
  'herd A entered paddock P3',
]

// Roles that default to Record mode; everyone else defaults to Ask.
const RECORDER_ROLES = ['herdsman', 'manager', 'land_monitor', 'biochar_operator', 'bush_clearer']
// Read-only roles can't record at all (backend 403s them). Owner joined in
// S6: the owner experience is verify-and-approve, not data entry.
const READONLY_ROLES = ['viewer', 'external_auditor', 'owner']

// Tailwind's preflight strips list bullets/margins, so map markdown elements to
// explicit compact chat styles (no typography plugin needed).
const MD_COMPONENTS = {
  p: ({ node, ...p }) => <p className="mb-2 last:mb-0" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-5 mb-2 last:mb-0 space-y-0.5" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-5 mb-2 last:mb-0 space-y-0.5" {...p} />,
  li: ({ node, ...p }) => <li className="leading-snug" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-semibold" {...p} />,
  a: ({ node, ...p }) => <a className="underline" target="_blank" rel="noreferrer" {...p} />,
  code: ({ node, ...p }) => <code className="rounded bg-earth-100 px-1 py-0.5 text-[0.85em]" {...p} />,
  table: ({ node, ...p }) => <table className="my-2 w-full text-left border-collapse" {...p} />,
  th: ({ node, ...p }) => <th className="border-b border-earth-200 pr-3 py-1 font-semibold" {...p} />,
  td: ({ node, ...p }) => <td className="border-b border-earth-100 pr-3 py-1 align-top" {...p} />,
}

function ChatBubble({ from, text, error }) {
  const isUser = from === 'user'
  return (
    <div className={clsx('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={clsx(
          'max-w-[85%] rounded-2xl px-4 py-2.5 text-sm break-words',
          isUser
            ? 'bg-primary-600 text-white rounded-br-sm whitespace-pre-wrap'
            : error
              ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-sm whitespace-pre-wrap'
              : 'bg-white text-gray-800 border border-earth-200 rounded-bl-sm',
        )}
      >
        {isUser || error
          ? text
          : <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{text}</ReactMarkdown>}
      </div>
    </div>
  )
}

function TypingDots() {
  return (
    <div className="flex justify-start">
      <div className="bg-white text-earth-400 border border-earth-200 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm">
        <span className="inline-flex gap-1">
          <span className="animate-bounce">•</span>
          <span className="animate-bounce [animation-delay:0.15s]">•</span>
          <span className="animate-bounce [animation-delay:0.3s]">•</span>
        </span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Weekly "pulse" digest — at-a-glance status + KPI scoreboard
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Ask mode (Q&A chat). The old owner-digest sidebar is gone — the Tuesday
// Report (linked from TuesdayReportCard below) replaced the weekly digest as
// the owner's summary artifact (Ernest, 2026-07-04).
// ---------------------------------------------------------------------------

// Suggested questions seeded from the latest Tuesday Report's frozen facts —
// the chips point at whatever actually moved this week. Falls back to the
// generic STARTERS when there is no report yet or nothing stands out.
function deriveStarters(facts) {
  if (!facts || typeof facts !== 'object') return STARTERS
  const out = []
  const sc = facts.scorecard
  const failed = Object.values(sc?.streams || {}).filter((s) => s.measured && !s.pass)
  if (failed.length) out.push(`Why did the "${failed[0].label}" check fail this week?`)
  const rec = facts.herd_reconciliation
  if (rec && rec.residual !== 0) out.push("Why doesn't the herd count reconcile this week?")
  if ((facts.unseen_animals?.excess_unseen || 0) > 0) out.push("Which animals haven't been seen lately?")
  if ((facts.health?.deaths_detail || []).length) out.push('What happened to the animals we lost this week?')
  if ((facts.weights_by_class?.overdue_for_weighing || 0) > 0) out.push('Which animals are overdue for weighing?')
  // Phase 4a: registration at the crush, when the ranch has a SmartKraal feed.
  if (facts.registration?.available) {
    out.push((facts.registration.sent_back_open || 0) > 0
      ? 'Which of my animals did the reviewer send back?'
      : 'Are my animals registered at the crush?')
  }
  if ((facts.money?.missing_receipt_count || 0) > 0) out.push('Which expenses are missing receipts?')
  const net = facts.finance?.net
  if (typeof net === 'number') {
    out.push(net < 0 ? 'Why was net cash negative last week?' : 'What drove last week’s income?')
  }
  for (const s of STARTERS) {
    if (out.length >= 4) break
    out.push(s)
  }
  return out.slice(0, 4)
}

// Compact card linking to the latest Tuesday Report, with the scorecard
// verdict when the report carries one. Sits at the top of the owner's
// assistant home.
function TuesdayReportCard({ report }) {
  if (!report) return null
  const sc = report.facts?.scorecard
  return (
    <Link to="/reports/weekly"
      className="mb-4 flex items-center justify-between rounded-xl border border-earth-200 bg-white px-4 py-3 hover:border-primary-300 hover:bg-primary-50/40 transition-colors">
      <div className="flex items-center gap-3">
        <FileText className="h-5 w-5 text-primary-600" />
        <div>
          <p className="text-sm font-medium text-gray-900">The Tuesday Report</p>
          <p className="text-xs text-earth-500">{report.period_label}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {sc && (
          <span className={clsx('px-2.5 py-0.5 rounded-full text-xs font-bold text-white',
            sc.overall_pass ? 'bg-green-600' : 'bg-red-600')}>
            {sc.overall_pass ? 'PASS' : 'FAIL'}
          </span>
        )}
        <ChevronRight className="h-4 w-4 text-earth-400" />
      </div>
    </Link>
  )
}

function AskPanel({ ranchId, isOwner, starters = STARTERS, language = 'en' }) {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [streamText, setStreamText] = useState('')
  const [streamTool, setStreamTool] = useState(null)
  const [recording, setRecording] = useState(false)
  const scrollRef = useRef(null)
  const recorderRef = useRef(null)
  const chunksRef = useRef([])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, streaming, streamText, streamTool])

  const send = async (question) => {
    const q = (question ?? input).trim()
    if (!q || streaming) return
    setMessages((m) => [...m, { from: 'user', text: q }])
    setInput('')
    setStreaming(true); setStreamText(''); setStreamTool(null)
    let acc = ''
    let errText = null
    try {
      await askAssistantStream(ranchId, q, language, {
        onTool: () => { acc = ''; setStreamText(''); setStreamTool('lookup') },
        onToken: (t) => { acc += t; setStreamTool(null); setStreamText(acc) },
        onError: (t) => { errText = t },
      })
      setMessages((m) => [...m, { from: 'assistant', text: errText || acc || '…', error: !!errText }])
    } catch {
      // Transport failure — fall back to the non-streaming endpoint.
      try {
        const data = await askAssistant(ranchId, q, language)
        setMessages((m) => [...m, { from: 'assistant', text: data?.message || '…' }])
      } catch {
        setMessages((m) => [...m, { from: 'assistant', error: true, text: "Sorry, I couldn't answer that just now. Please try again." }])
      }
    } finally {
      setStreaming(false); setStreamText(''); setStreamTool(null)
    }
  }

  // Voice question: record → transcribe (language-hinted) → answer.
  const startVoice = async () => {
    const mime = pickAudioMime()
    if (!navigator.mediaDevices?.getUserMedia || !mime) {
      setMessages((m) => [...m, { from: 'assistant', error: true,
        text: 'Voice questions are not supported in this browser — please type instead.' }])
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mr = new MediaRecorder(stream, { mimeType: mime })
      chunksRef.current = []
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data) }
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop())
        setRecording(false)
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' })
        if (!blob.size) return
        setStreaming(true); setStreamTool('lookup')
        try {
          const data = await askAssistantVoice(ranchId, blob, language)
          if (data?.status === 'error' || !data?.transcript) {
            setMessages((m) => [...m, { from: 'assistant', error: true,
              text: data?.message || 'Could not understand the audio — please try again.' }])
          } else {
            setMessages((m) => [...m,
              { from: 'user', text: `🎤 ${data.transcript}` },
              { from: 'assistant', text: data.message || '…' }])
          }
        } catch {
          setMessages((m) => [...m, { from: 'assistant', error: true,
            text: "Sorry, I couldn't process that voice note. Please try again." }])
        } finally {
          setStreaming(false); setStreamTool(null)
        }
      }
      recorderRef.current = mr
      mr.start()
      setRecording(true)
    } catch {
      setMessages((m) => [...m, { from: 'assistant', error: true,
        text: 'Microphone access was denied.' }])
    }
  }

  const stopVoice = () => { try { recorderRef.current?.stop() } catch { /* already stopped */ } }

  return (
    <div className="grid gap-6 grid-cols-1">
      <div className="flex flex-col">
        <div ref={scrollRef}
          className="flex-1 min-h-[420px] max-h-[60vh] overflow-y-auto rounded-xl border border-earth-200 bg-earth-50/50 p-4 space-y-3">
          {messages.length === 0 && !streaming && (
            <div className="h-full flex flex-col items-center justify-center text-center text-earth-500 gap-4 py-8">
              <Sparkles className="h-8 w-8 text-primary-400" />
              <p className="text-sm max-w-sm">Ask a question about your ranch, or try one of these:</p>
              <div className="flex flex-wrap justify-center gap-2 max-w-md">
                {starters.map((s) => (
                  <button key={s} onClick={() => send(s)}
                    className="rounded-full border border-earth-200 bg-white px-3 py-1.5 text-xs text-gray-700 hover:border-primary-300 hover:bg-primary-50 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => <ChatBubble key={i} from={m.from} text={m.text} error={m.error} />)}
          {streaming && streamTool && (
            <div className="flex justify-start">
              <div className="bg-white text-earth-500 border border-earth-200 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm">
                Looking up your ranch data…
              </div>
            </div>
          )}
          {streaming && !streamTool && streamText && <ChatBubble from="assistant" text={streamText} />}
          {streaming && !streamTool && !streamText && <TypingDots />}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send() }} className="mt-3 flex items-center gap-2">
          <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your ranch…"
            className="flex-1 rounded-xl border border-earth-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400" />
          <button type="button" onClick={recording ? stopVoice : startVoice} disabled={streaming}
            className={clsx('inline-flex items-center justify-center rounded-xl h-11 w-11 transition-colors',
              recording ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse'
                        : 'bg-earth-100 text-earth-700 hover:bg-earth-200 disabled:opacity-40')}
            aria-label={recording ? 'Stop recording' : 'Ask by voice'}
            title={recording ? 'Stop and send' : 'Ask by voice (English, Luganda or Runyankole)'}>
            {recording ? <Square className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </button>
          <button type="submit" disabled={!input.trim() || streaming}
            className="inline-flex items-center justify-center rounded-xl bg-primary-600 text-white h-11 w-11 disabled:opacity-40 hover:bg-primary-700 transition-colors"
            aria-label="Send">
            <Send className="h-5 w-5" />
          </button>
        </form>
        <p className="mt-2 flex items-center gap-1.5 text-xs text-earth-400">
          <AlertCircle className="h-3.5 w-3.5" />
          Read-only — the Assistant answers questions; it does not change any records.
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Record mode (confirm-then-commit data collection)
// ---------------------------------------------------------------------------

function ConfirmCard({ summary, onConfirm, onCancel, disabled }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-2xl rounded-bl-sm border border-amber-200 bg-amber-50 px-4 py-3">
        <p className="text-sm text-gray-900 font-medium">{summary}</p>
        <div className="mt-3 flex gap-2">
          <button onClick={onConfirm} disabled={disabled}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 text-white px-3 py-1.5 text-sm hover:bg-primary-700 disabled:opacity-40 transition-colors">
            <Check className="h-4 w-4" /> Confirm
          </button>
          <button onClick={onCancel} disabled={disabled}
            className="inline-flex items-center gap-1.5 rounded-lg border border-earth-300 bg-white text-gray-700 px-3 py-1.5 text-sm hover:bg-earth-50 disabled:opacity-40 transition-colors">
            <X className="h-4 w-4" /> Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

function RecordPanel({ ranchId }) {
  const [messages, setMessages] = useState([])  // {from:'user'|'assistant', text, error?}
  const [input, setInput] = useState('')
  const [pending, setPending] = useState(null)  // {summary} when awaiting confirmation
  const scrollRef = useRef(null)

  const [recording, setRecording] = useState(false)
  const [photoReq, setPhotoReq] = useState(null)  // {event_id, animal_name} after a death record
  const mediaRef = useRef(null)
  const chunksRef = useRef([])

  const recordMut = useMutation({ mutationFn: (txt) => recordAssistant(ranchId, txt) })
  const confirmMut = useMutation({ mutationFn: (reply) => confirmAssistant(ranchId, reply) })
  const voiceMut = useMutation({ mutationFn: (blob) => recordVoice(ranchId, blob) })
  const photoMut = useMutation({ mutationFn: ({ eventId, file }) => recordPhoto(ranchId, eventId, file) })
  const busy = recordMut.isPending || confirmMut.isPending || voiceMut.isPending || photoMut.isPending

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, pending, busy])

  // Apply a structured result: show a confirmation card, or echo the message.
  const apply = (j) => {
    if (j.status === 'awaiting_confirmation') {
      setPending({ summary: j.summary || j.message })
      return
    }
    setPending(null)
    setMessages((m) => [...m, { from: 'assistant', text: j.message, error: j.status === 'error' }])
    if (j.status === 'committed' && j.photo_request) setPhotoReq(j.photo_request)
  }

  const uploadPhoto = (file) => {
    const req = photoReq
    if (!req || busy) return
    setPhotoReq(null)
    photoMut.mutate({ eventId: req.event_id, file }, {
      onSuccess: () => setMessages((m) => [...m, { from: 'assistant', text: '📷 Photo attached.' }]),
      onError: () => setMessages((m) => [...m, { from: 'assistant', error: true, text: "Couldn't attach the photo. Please try again." }]),
    })
  }

  const send = (text) => {
    const t = (text ?? input).trim()
    if (!t || busy) return
    setMessages((m) => [...m, { from: 'user', text: t }])
    setInput('')
    setPending(null)
    recordMut.mutate(t, {
      onSuccess: apply,
      onError: () => setMessages((m) => [...m, { from: 'assistant', error: true, text: "Couldn't record that. Please try again." }]),
    })
  }

  const resolve = (reply) => {
    if (busy) return
    setPending(null)
    confirmMut.mutate(reply, {
      onSuccess: apply,
      onError: () => setMessages((m) => [...m, { from: 'assistant', error: true, text: "Couldn't save. Please try again." }]),
    })
  }

  const startRecording = async () => {
    if (recording || busy) return
    const mime = pickAudioMime()
    if (!navigator.mediaDevices?.getUserMedia || !mime) {
      // No Opus support (e.g. Safari records AAC the transcriber can't read).
      setMessages((m) => [...m, { from: 'assistant', error: true, text: 'Voice recording is not supported in this browser.' }])
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mr = new MediaRecorder(stream, { mimeType: mime })
      chunksRef.current = []
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data) }
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop())
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' })
        setPending(null)
        voiceMut.mutate(blob, {
          onSuccess: (j) => {
            if (j.transcript) setMessages((m) => [...m, { from: 'user', text: `🎤 ${j.transcript}` }])
            apply(j)
          },
          onError: () => setMessages((m) => [...m, { from: 'assistant', error: true, text: "Couldn't transcribe that. Please try again." }]),
        })
      }
      mediaRef.current = mr
      mr.start()
      setRecording(true)
    } catch {
      setMessages((m) => [...m, { from: 'assistant', error: true, text: 'Microphone access was denied.' }])
    }
  }

  const stopRecording = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop()
      setRecording(false)
    }
  }

  return (
    <div className="max-w-3xl">
      <div ref={scrollRef}
        className="min-h-[420px] max-h-[60vh] overflow-y-auto rounded-xl border border-earth-200 bg-earth-50/50 p-4 space-y-3">
        {messages.length === 0 && !pending && (
          <div className="h-full flex flex-col items-center justify-center text-center text-earth-500 gap-4 py-8">
            <ClipboardList className="h-8 w-8 text-primary-400" />
            <p className="text-sm max-w-sm">
              Type what happened in plain words (or a short command). I'll read it back for you to confirm before saving.
            </p>
            <div className="flex flex-wrap justify-center gap-2 max-w-md">
              {RECORD_HINTS.map((s) => (
                <button key={s} onClick={() => setInput(s)}
                  className="rounded-full border border-earth-200 bg-white px-3 py-1.5 text-xs text-gray-700 hover:border-primary-300 hover:bg-primary-50 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => <ChatBubble key={i} from={m.from} text={m.text} error={m.error} />)}
        {busy && <TypingDots />}
        {pending && !busy && (
          <ConfirmCard summary={pending.summary} disabled={busy}
            onConfirm={() => resolve('yes')} onCancel={() => resolve('no')} />
        )}
        {photoReq && !busy && (
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-2xl rounded-bl-sm border border-blue-200 bg-blue-50 px-4 py-3">
              <p className="text-sm text-gray-900">
                Add a photo as evidence for the death of <b>{photoReq.animal_name}</b>?
              </p>
              <div className="mt-2 flex items-center gap-3">
                <label className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 text-white px-3 py-1.5 text-sm hover:bg-primary-700 cursor-pointer transition-colors">
                  <Camera className="h-4 w-4" /> Choose photo
                  <input type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f) }} />
                </label>
                <button onClick={() => setPhotoReq(null)} className="text-sm text-earth-500 hover:text-gray-700">
                  Skip
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send() }} className="mt-3 flex items-center gap-2">
        <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder={recording ? 'Listening… tap stop when done' : 'Record an event — e.g. “weight Bessie 350”…'}
          disabled={recording}
          className="flex-1 rounded-xl border border-earth-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 disabled:bg-earth-50" />
        <button type="button" onClick={recording ? stopRecording : startRecording} disabled={busy && !recording}
          className={clsx('inline-flex items-center justify-center rounded-xl h-11 w-11 transition-colors disabled:opacity-40',
            recording ? 'bg-red-600 text-white animate-pulse' : 'border border-earth-300 text-earth-600 hover:bg-earth-50')}
          aria-label={recording ? 'Stop recording' : 'Record voice'}>
          {recording ? <Square className="h-4 w-4" /> : <Mic className="h-5 w-5" />}
        </button>
        <button type="submit" disabled={!input.trim() || busy || recording}
          className="inline-flex items-center justify-center rounded-xl bg-primary-600 text-white h-11 w-11 disabled:opacity-40 hover:bg-primary-700 transition-colors"
          aria-label="Record">
          <Send className="h-5 w-5" />
        </button>
      </form>
      <p className="mt-2 flex items-center gap-1.5 text-xs text-earth-400">
        <AlertCircle className="h-3.5 w-3.5" />
        Nothing is saved until you Confirm. Records may need manager approval.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function timeOfDayGreeting() {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

export default function AssistantPage({ ranchId }) {
  const { role, user, ranch } = useAuth()
  const isOwner = ['owner', 'system_admin'].includes(role)
  const canRecord = !READONLY_ROLES.includes(role)
  const [mode, setMode] = useState(RECORDER_ROLES.includes(role) ? 'record' : 'ask')

  // Answer language: profile preference first, then this device's last
  // choice. Changing it persists to the profile (best-effort) so WhatsApp
  // answers follow the same language.
  const langKey = `assistant.language.${user?.id || 'anon'}`
  const [language, setLanguage] = useState(() => {
    const codes = LANGUAGES.map((l) => l.code)
    if (codes.includes(user?.language_pref)) return user.language_pref
    try {
      const stored = localStorage.getItem(langKey)
      if (codes.includes(stored)) return stored
    } catch { /* localStorage unavailable */ }
    return 'en'
  })
  const pickLanguage = (code) => {
    setLanguage(code)
    try { localStorage.setItem(langKey, code) } catch { /* ignore */ }
    if (user?.id) {
      updateUser(user.id, { language_pref: code }, ranchId, user.id).catch(() => {})
    }
  }
  // The profile query resolves after mount — adopt its preference once it
  // lands, unless this device already made an explicit choice.
  useEffect(() => {
    const pref = user?.language_pref
    if (!LANGUAGES.some((l) => l.code === pref)) return
    let chosen = null
    try { chosen = localStorage.getItem(langKey) } catch { /* ignore */ }
    if (!chosen && pref !== language) setLanguage(pref)
  }, [user?.language_pref])

  // Owner home: latest Tuesday Report card + question chips seeded from its
  // frozen facts. Two small queries, owner-scope only.
  const { data: reportList } = useQuery({
    queryKey: ['weeklyReports', ranchId],
    queryFn: () => getWeeklyReports(ranchId),
    enabled: !!ranchId && isOwner,
  })
  const latestId = reportList?.items?.[0]?.id
  const { data: latestReport } = useQuery({
    queryKey: ['weeklyReport', latestId],
    queryFn: () => getWeeklyReport(latestId),
    enabled: !!latestId && isOwner,
  })
  const starters = isOwner ? deriveStarters(latestReport?.facts) : STARTERS

  const firstName = (user?.full_name || '').split(' ')[0]
  const title = isOwner && firstName ? `${timeOfDayGreeting()}, ${firstName}` : 'Assistant'
  const subtitle = isOwner
    ? `Here's ${ranch?.name || 'your ranch'} — ask me anything about it.`
    : 'Ask about your ranch, or record what happened in plain words.'

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} icon={Sparkles} />

      {isOwner && <TuesdayReportCard report={latestReport} />}
      {/* Plan B2: the last dip day at the kraal, with faces, under the report card */}
      {isOwner && <KraalFeedCard ranchId={ranchId} className="mb-4" />}

      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        {canRecord ? (
          <div className="inline-flex rounded-lg border border-earth-200 bg-earth-50 p-0.5">
            {[['record', 'Record'], ['ask', 'Ask']].map(([m, label]) => (
              <button key={m} onClick={() => setMode(m)}
                className={clsx('px-4 py-1.5 text-sm rounded-md transition-colors',
                  mode === m ? 'bg-white shadow text-gray-900 font-medium' : 'text-earth-500 hover:text-gray-700')}>
                {label}
              </button>
            ))}
          </div>
        ) : <span />}
        <div className="inline-flex rounded-lg border border-earth-200 bg-earth-50 p-0.5"
             title="Answer language — voice questions are understood in the same language">
          {LANGUAGES.map((l) => (
            <button key={l.code} onClick={() => pickLanguage(l.code)}
              className={clsx('px-3 py-1.5 text-sm rounded-md transition-colors',
                language === l.code ? 'bg-white shadow text-gray-900 font-medium' : 'text-earth-500 hover:text-gray-700')}>
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {mode === 'record' && canRecord
        ? <RecordPanel ranchId={ranchId} />
        : <AskPanel ranchId={ranchId} isOwner={isOwner} starters={starters} language={language} />}
    </div>
  )
}
